import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useCopyOverride, useColorOverride, COPY_DEFAULTS } from "@/lib/copyOverrides";
import {
  amenitiesForAccommodations,
  parseAccommodationIds,
} from "@/lib/accommodationAmenities";
import { getPopupImage } from "@/lib/popupImages";
import { parsePopupCards } from "@/lib/popupCards";

interface Props {
  onCta?: () => void;
  interactive?: boolean;
}

const ROTATE_MS = 4000;

function parseImageUrls(csv: string): string[] {
  return csv
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^https:\/\//i.test(s));
}

const RemoteImages = ({ urls }: { urls: string[] }) => {
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const live = urls.filter((u) => !failed[u]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  const current = live.length ? index % live.length : 0;

  useEffect(() => {
    if (live.length < 2 || paused) return;
    const id = window.setInterval(() => setIndex((i) => i + 1), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [live.length, paused]);

  if (!live.length) return null;

  const next = live[(current + 1) % live.length];

  return (
    <div
      className="relative w-full aspect-[16/9] bg-muted overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        if (start == null || live.length < 2) return;
        const dx = (e.changedTouches[0]?.clientX ?? start) - start;
        if (Math.abs(dx) < 40) return;
        setIndex((i) => (dx < 0 ? i + 1 : i - 1 + live.length));
      }}
    >
      {live.map((url, i) => (
        <img
          key={url}
          src={url}
          alt=""
          referrerPolicy="no-referrer"
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
          onError={() => setFailed((f) => ({ ...f, [url]: true }))}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      {live.length > 1 && (
        <>
          <link rel="preload" as="image" href={next} />
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {live.map((url, i) => (
              <button
                key={url}
                type="button"
                aria-label={`Show image ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === current ? "bg-white" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const OvernightPopupCard = ({ onCta, interactive = true }: Props) => {
  const headline = useCopyOverride("popup.headline", COPY_DEFAULTS["popup.headline"]);
  const price = useCopyOverride("popup.price", COPY_DEFAULTS["popup.price"]);
  const subline = useCopyOverride("popup.subline", COPY_DEFAULTS["popup.subline"]);
  const ctaLabel = useCopyOverride("popup.cta", COPY_DEFAULTS["popup.cta"]);
  const accomCsv = useCopyOverride(
    "popup.accommodations",
    COPY_DEFAULTS["popup.accommodations"]
  );
  const imageId = useCopyOverride("popup.image", COPY_DEFAULTS["popup.image"]);
  const imagesCsv = useCopyOverride("popup.images", COPY_DEFAULTS["popup.images"]);
  const cardsRaw = useCopyOverride("popup.cards", COPY_DEFAULTS["popup.cards"]);
  const cards = useMemo(() => parsePopupCards(cardsRaw), [cardsRaw]);
  const accent = useColorOverride("popup.accent");
  const buttonFg = useColorOverride("popup.buttonFg");
  const image = getPopupImage(imageId);
  const amenities = amenitiesForAccommodations(parseAccommodationIds(accomCsv));
  const remoteUrls = useMemo(() => parseImageUrls(imagesCsv), [imagesCsv]);

  return (
    <div className="w-full">
      {remoteUrls.length > 0 ? (
        <RemoteImages urls={remoteUrls} />
      ) : (
        <div className="relative w-full aspect-[16/9] bg-muted">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </div>
      )}
      <div className="px-5 sm:px-7 pt-5 pb-6 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary leading-tight whitespace-pre-wrap">
          {headline}
        </h2>
        <p
          style={accent ? { color: accent } : undefined}
          className={`mt-3 text-xl sm:text-2xl font-bold ${accent ? "" : "text-accent"} whitespace-pre-wrap`}
        >
          {price}
        </p>
        <p className="mt-4 text-lg sm:text-2xl text-foreground/80 font-medium whitespace-pre-wrap">
          {subline}
        </p>
        {cards.length > 0 ? (
          <div className="mt-4 space-y-4 text-left">
            {cards.map((card) => (
              <div key={card.id} className="rounded-xl border border-border overflow-hidden">
                {card.image && (
                  <img
                    src={card.image}
                    alt={card.title}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-[16/9] object-cover"
                  />
                )}
                <div className="p-3">
                  {card.title && (
                    <p className="font-heading text-base sm:text-lg font-bold text-primary text-center">
                      {card.title}
                    </p>
                  )}
                  {card.price && (
                    <p
                      style={accent ? { color: accent } : undefined}
                      className={`text-sm font-bold ${accent ? "" : "text-accent"}`}
                    >
                      {card.price}
                    </p>
                  )}
                  {card.bullets.length > 0 && (
                    <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                      {card.bullets.map((b, i) => (
                        <li key={`${card.id}-${i}`} className="flex items-center gap-2 text-sm text-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          amenities.length > 0 && (
            <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-left max-w-sm mx-auto">
              {amenities.map(({ Icon, label, id }) => (
                <li key={id} className="flex items-center gap-2 text-sm text-foreground">
                  <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          )
        )}
        <Button
          onClick={interactive ? onCta : undefined}
          disabled={!interactive}
          style={{
            ...(accent ? { backgroundColor: accent } : {}),
            ...(buttonFg ? { color: buttonFg } : {}),
          }}
          className={`w-full mt-6 ${
            accent ? "" : "bg-primary hover:bg-primary/90"
          } text-primary-foreground font-bold uppercase tracking-wide text-base py-6 rounded-xl shadow-lg disabled:opacity-100`}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

export default OvernightPopupCard;
